// @file OneDrive store
import { trackEvent } from '@@/bits/analytics'
import { getHyphenatedCurrentLocale } from '@@/bits/current_locale'
import { getCurrentUser } from '@@/bits/current_user'
import { captureException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { ONE_MB } from '@@/bits/numbers'
import { MAX_FILE_UPLOAD_BYTES_SIZE } from '@@/bits/uploader'
import { isRegistered } from '@@/bits/user_model'
import { ApiErrorCode, SnackbarNotificationType } from '@@/enums'
import oneDriveColorSvg from '@@/images/microsoft_onedrive_color.svg'
import type { ConfirmationDialogPayload } from '@@/pinia/global_confirmation_dialog'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useMicrosoftOneDriveUploadsStore } from '@@/pinia/microsoft_onedrive_uploads_store'
import { UserIntegrationsApi } from '@@/surface/api/user_integrations'
import type { MicrosoftOneDriveAuthorizationResult } from '@@/types'
import { useMicrosoftOneDrive } from '@@/vuecomposables/microsoft_onedrive'
import type { JsonAPIResource } from '@padlet/arvo'
import { uniqueId } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

enum MicrosoftOneDriveFetchingStatus {
  Loading = 'Loading',
  Completed = 'Completed',
  Errored = 'Errored',
}

enum MicrosoftOneDriveAccountType {
  Personal = 'personal',
  Business = 'business',
}

enum MicrosoftOneDriveUploadType {
  Url = 'url',
  File = 'file',
}

enum MicrosoftOneDriveAcceptedFileTypes {
  All = 'all',
  Images = 'images',
  ImagesVideos = 'images_videos',
}

// Specify required properties from OneDrive picker based on docs: https://learn.microsoft.com/en-us/onedrive/developer/controls/file-pickers/?view=odsp-graph-online#picked-item-results
interface MicrosoftOneDrivePickedFile {
  // properties appear in all selected files
  id: string
  parentReference: {
    driveId: string
    [key: string]: unknown
  }
  '@sharePoint.endpoint': string
  // common file properties
  name?: string
  webUrl?: string
  size?: number
  // package is how the file picker identifies OneNote files on sharepoint (business accounts)
  package?: {
    type: string
    [key: string]: unknown
  }
  // any additional unknown properties
  [key: string]: unknown
}

const useMicrosoftOneDrivePickerStore = defineStore('microsoftOneDrivePickerStore', () => {
  const microsoftOneDriveUploadsStore = useMicrosoftOneDriveUploadsStore()
  // #region: configuration
  const microsoftOneDriveFilepickerBaseUrl = ref<string | undefined>(undefined)
  const microsoftOneDriveByPathListUrl = ref<string | undefined>(undefined)

  // Create picker options for OneDrive
  const acceptedFileTypes = ref<MicrosoftOneDriveAcceptedFileTypes | null>(null)
  const acceptedFileTypesConfigOption = computed(() => {
    switch (acceptedFileTypes.value) {
      case MicrosoftOneDriveAcceptedFileTypes.All:
        return []
      case MicrosoftOneDriveAcceptedFileTypes.Images:
        return ['photo']
      case MicrosoftOneDriveAcceptedFileTypes.ImagesVideos:
        return ['photo', 'video']
      default:
        return []
    }
  })
  const pickerOptions = computed(() => ({
    sdk: '8.0',
    entry: {
      oneDrive: {
        files: {},
      },
      // we use oneDrive instead of sharePoint because we want the picker to start on the users files (not organization files)
      // leaving this here for reference
      // sharePoint: {
      //   byPath: microsoftOneDriveByPathListUrl.value,
      // },
    },
    authentication: {},
    messaging: {
      origin: window.location.origin,
      channelId: uniqueId('ms-onedrive-file-picker-channel-'),
    },
    search: {
      enabled: true,
    },
    typesAndSources: {
      mode: 'files',
      filters: acceptedFileTypesConfigOption.value,
      pivots: {
        oneDrive: true,
        recent: true,
        // NOTE: We're setting the shared tab to false since this tab requires Files.Read.All scope
        // Adding the Files.Read.All scope changes the auth flow to require admin consent, which adds too much friction for connecting to OneDrive
        // Until we find a workaround (if it exists), keep this shared tab disabled
        shared: false,
        myOrganization: false,
        site: false,
      },
    },
    selection: {
      mode: 'single',
    },
    // these are necessary to hide the upload and create folder buttons on personal file picker
    commands: {
      upload: {
        enabled: false,
      },
      createFolder: {
        enabled: false,
      },
    },
    accessibility: {
      enableFocusTrap: true,
      trapFocusOnLoad: true,
    },
  }))

  // Convert picker options object to query string
  const queryString = computed(
    () =>
      new URLSearchParams({
        filePicker: asciiSafeStringify(pickerOptions.value),
        locale: getHyphenatedCurrentLocale(),
      }),
  )

  // for business accounts we can use the documented file picker URL: https://learn.microsoft.com/en-us/onedrive/developer/controls/file-pickers/?view=odsp-graph-online#initiate-the-picker
  // however for personal accounts this doesn't work, so we use the undocumented https://onedrive.live.com/picker
  const pickerUrlSegment = computed(() => {
    return microsoftOneDriveAccountType.value === MicrosoftOneDriveAccountType.Business
      ? '/_layouts/15/FilePicker.aspx'
      : ''
  })

  // construct OneDrive picker URL with query parameters
  const fullPickerUrl = computed(() => {
    if (microsoftOneDriveFilepickerBaseUrl.value !== undefined) {
      const url = new URL(microsoftOneDriveFilepickerBaseUrl.value)
      const existingPathname = url.pathname
      // append file picker url segment (for business accounts)
      url.pathname = existingPathname + pickerUrlSegment.value
      url.search = queryString.value.toString()
      return url.toString()
    } else {
      captureException(new Error('Undefined file picker base URL'))
      return ''
    }
  })
  // #endregion

  // #region: authorization and fetching status
  const microsoftOneDriveAccessToken = ref<string | null>(null)
  const microsoftOneDriveAccountType = ref<MicrosoftOneDriveAccountType | undefined>(undefined)
  const microsoftOneDriveEmail = ref<string | null>(null)
  const isUserLinkedWithMicrosoftOneDrive = computed(() => microsoftOneDriveEmail.value != null)

  const microsoftOneDriveFetchingStatus = ref(MicrosoftOneDriveFetchingStatus.Loading)
  const isFetchingMicrosoftOneDriveUserInformation = computed(
    () => microsoftOneDriveFetchingStatus.value === MicrosoftOneDriveFetchingStatus.Loading,
  )
  const isErroredFetchingMicrosoftOneDriveUserInformation = computed(
    () => microsoftOneDriveFetchingStatus.value === MicrosoftOneDriveFetchingStatus.Errored,
  )

  // Helper function to set Microsoft OneDrive info from fetch or refresh API response
  function setMicrosoftOneDriveInfo(microsoftOneDriveInfo: MicrosoftOneDriveAuthorizationResult): void {
    microsoftOneDriveEmail.value = microsoftOneDriveInfo.linkedEmail
    microsoftOneDriveAccessToken.value = microsoftOneDriveInfo.accessToken
    microsoftOneDriveAccountType.value = microsoftOneDriveInfo.accountType as MicrosoftOneDriveAccountType
    microsoftOneDriveFilepickerBaseUrl.value = microsoftOneDriveInfo.filepickerBaseUrl
    microsoftOneDriveByPathListUrl.value = microsoftOneDriveInfo.byPathListUrl
  }

  function clearMicrosoftOneDriveInfo(): void {
    microsoftOneDriveEmail.value = null
    microsoftOneDriveAccessToken.value = null
    microsoftOneDriveAccountType.value = undefined
    microsoftOneDriveFilepickerBaseUrl.value = undefined
    microsoftOneDriveByPathListUrl.value = undefined
  }

  async function fetchMicrosoftOneDriveInfo(): Promise<void> {
    const currentUser = getCurrentUser()

    // Don't fetch for anonymous users
    if (!isRegistered(currentUser)) {
      microsoftOneDriveFetchingStatus.value = MicrosoftOneDriveFetchingStatus.Completed
      return
    }

    try {
      microsoftOneDriveFetchingStatus.value = MicrosoftOneDriveFetchingStatus.Loading
      const response = await UserIntegrationsApi.fetchMicrosoftOneDriveAuthorization(currentUser.id)
      const microsoftOneDriveInfo = (response.data as JsonAPIResource<MicrosoftOneDriveAuthorizationResult> | undefined)
        ?.attributes
      if (microsoftOneDriveInfo != null) {
        setMicrosoftOneDriveInfo(microsoftOneDriveInfo)
      }
      microsoftOneDriveFetchingStatus.value = MicrosoftOneDriveFetchingStatus.Completed
    } catch (e) {
      const error = JSON.parse(e.message)?.errors[0]
      if (error.code === ApiErrorCode.TOKEN_EXPIRED) {
        // if access token is expired while fetching token info, request a new access token
        await refreshMicrosoftOneDriveAccessToken()
      } else {
        void useGlobalSnackbarStore().genericFetchError()
        microsoftOneDriveFetchingStatus.value = MicrosoftOneDriveFetchingStatus.Errored
      }
    }
  }

  async function refreshMicrosoftOneDriveAccessToken(): Promise<void> {
    const currentUser = getCurrentUser()
    try {
      // get new access token for the current user, then set email and access token
      const refreshedResponse = await UserIntegrationsApi.refreshMicrosoftOneDriveAccessToken(currentUser.id)
      const microsoftOneDriveInfo = (
        refreshedResponse.data as JsonAPIResource<MicrosoftOneDriveAuthorizationResult> | undefined
      )?.attributes
      if (microsoftOneDriveInfo != null) {
        setMicrosoftOneDriveInfo(microsoftOneDriveInfo)
      }
      microsoftOneDriveFetchingStatus.value = MicrosoftOneDriveFetchingStatus.Completed
    } catch (e) {
      microsoftOneDriveFetchingStatus.value = MicrosoftOneDriveFetchingStatus.Errored

      const error = JSON.parse(e.message)?.errors[0]
      if (error.code === ApiErrorCode.TOKEN_EXPIRED || error.code === ApiErrorCode.UPDATE_FAILED) {
        void useGlobalSnackbarStore().setSnackbar({
          message: __('Your Microsoft OneDrive credentials have expired. Please try again to re-authorize'),
          timeout: 5000,
          notificationType: SnackbarNotificationType.error,
        })
      } else {
        void useGlobalSnackbarStore().genericFetchError()
      }
    }
  }
  // #endregion

  // #region: message handling
  const messagePort = ref<MessagePort | null>(null)

  const handlePickCallbacks = ref<{
    onStart?: (url: string) => void
    onError?: () => void
    onProgress?: (progress: number) => void
    onSuccess?: (url: string) => void
  } | null>(null)

  const setupPickerMessageListener = (
    iframe: HTMLIFrameElement,
    callbacks?: {
      onStart?: (url: string) => void
      onError?: () => void
      onProgress?: (progress: number) => void
      onSuccess?: (url: string) => void
    },
  ): void => {
    handlePickCallbacks.value = callbacks ?? null
    // Listen for initial connection from picker
    const handleWindowMessage = (event: MessageEvent): void => {
      if (event.source !== iframe.contentWindow) return

      const message = event.data
      if (message.type === 'initialize') {
        // Establish MessagePort communication
        messagePort.value = event.ports[0]
        messagePort.value?.addEventListener('message', (event) => {
          void handleChannelMessage(event)
        })
        messagePort.value?.start()

        // Activate the picker
        messagePort.value?.postMessage({
          type: 'activate',
        })

        // Remove window listener after establishing MessagePort
        window.removeEventListener('message', handleWindowMessage)
      }
    }

    window.addEventListener('message', handleWindowMessage)
  }
  // general handler that handles different message types
  const handleChannelMessage = async (message: MessageEvent): Promise<void> => {
    const payload = message.data

    switch (payload.type) {
      case 'command': {
        // Acknowledge all commands
        messagePort.value?.postMessage({
          type: 'acknowledge',
          id: payload.id,
        })

        const command = payload.data

        switch (command.command) {
          // the authenticate command is sent when the picker is opened and whenever there is an action requiring the access token
          case 'authenticate': {
            await handleAuthenticate(payload.id)
            break
          }

          case 'pick': {
            await handlePickCommand(payload.id, command)
            break
          }

          case 'close': {
            unmountOneDrivePicker()
            break
          }

          default: {
            // Send error for unsupported commands
            messagePort.value?.postMessage({
              type: 'result',
              id: payload.id,
              data: {
                result: 'error',
                error: {
                  code: 'unsupportedCommand',
                  message: command.command,
                },
              },
            })
            break
          }
        }
        break
      }
    }
  }
  const handleAuthenticate = async (messageId: string): Promise<void> => {
    try {
      const token = microsoftOneDriveAccessToken.value

      if (!token) throw new Error('Unable to obtain access token')

      messagePort.value?.postMessage({
        type: 'result',
        id: messageId,
        data: {
          result: 'token',
          token,
        },
      })
    } catch (error: any) {
      messagePort.value?.postMessage({
        type: 'result',
        id: messageId,
        data: {
          result: 'error',
          error: {
            code: 'unableToObtainToken',
            message: error?.message ?? 'Unknown error',
          },
        },
      })
    }
  }
  const handlePick = async (file: MicrosoftOneDrivePickedFile): Promise<void> => {
    try {
      const fileId = file.id
      const fileName = file.name ?? ''
      let fileType = fileName.split('.').pop()?.toLowerCase() ?? ''
      const fileSize = file.size ?? 0
      const driveId = file.parentReference.driveId
      const endpoint = file['@sharePoint.endpoint']
      const webUrl = file.webUrl ?? ''
      // we embed our own cloud storage URL, so the file will render even if the original file is deleted
      // keeping this for reference if we ever switch to using the original embedUrl (note this value isn't provided for personal accounts)
      // const embedUrl = file['@sharePoint.embedUrl'] ?? null

      // For sharepoint (business accounts), OneNote files are indicated by package type 'oneNote'
      // thus, we set fileType to 'one' for so they're properly detected
      // (personal accounts do properly include the file extension .one)
      if (file.package?.type === 'oneNote') {
        fileType = 'one'
      }

      trackEvent('Microsoft OneDrive', 'Picked file', fileName, null, {
        fileId,
        fileType,
        fileSize,
        driveId,
        endpoint,
        webUrl,
      })

      const fileSizeOverMax =
        MAX_FILE_UPLOAD_BYTES_SIZE != null && fileSize != null && fileSize > MAX_FILE_UPLOAD_BYTES_SIZE + ONE_MB
      let uploadType: MicrosoftOneDriveUploadType = MicrosoftOneDriveUploadType.Url

      // For large files: don't download and upload the file, use SharePoint webUrl directly
      // For OneNote files: Graph API does not provide a download url, so we just attach webUrl
      if (fileSizeOverMax || fileType === 'one') {
        uploadType = MicrosoftOneDriveUploadType.Url
      }
      // otherwise, we download and upload the file, and start emit with the expected GCS URL
      else {
        uploadType = MicrosoftOneDriveUploadType.File
      }

      microsoftOneDriveUploadsStore.startUploadJob({
        uploadKey: uniqueId('ms-onedrive-upload-'),
        fileId,
        fileName,
        fileType,
        fileSize,
        driveId,
        endpoint,
        uploadType,
        webUrl,
        accessToken: microsoftOneDriveAccessToken.value ?? '',
        onStart: (url) => {
          handlePickCallbacks.value?.onStart?.(url)
        },
        onError: () => {
          handlePickCallbacks.value?.onError?.()
          unmountOneDrivePicker() // Fully close picker on error
        },
        onProgress: (progress) => {
          handlePickCallbacks.value?.onProgress?.(progress)
        },
        onSuccess: (response) => {
          handlePickCallbacks.value?.onSuccess?.(response.url)
          unmountOneDrivePicker() // Fully close picker on success
        },
      })
    } catch (error) {
      captureException(error)
      handlePickCallbacks.value?.onError?.()
      throw error
    }
  }
  const handlePickCommand = async (messageId: string, command: any): Promise<void> => {
    try {
      // Extract picked files from command
      const pickedFile = command.items[0] as MicrosoftOneDrivePickedFile
      // Call the pick handler
      await handlePick(pickedFile)
      // Send success response
      messagePort.value?.postMessage({
        type: 'result',
        id: messageId,
        data: {
          result: 'success',
        },
      })
      // Hide picker immediately after file selection, but keep it mounted for callbacks
      hideOneDrivePicker()
    } catch (error: any) {
      messagePort.value?.postMessage({
        type: 'result',
        id: messageId,
        data: {
          result: 'error',
          error: {
            code: 'pickFailed',
            message: error?.message ?? 'Unknown error',
          },
        },
      })
    }
  }

  // #endregion

  // #region: picker visibility
  const isMicrosoftOneDrivePickerOpen = ref<boolean>(false)
  const isMicrosoftOneDrivePickerVisible = ref<boolean>(false)

  const hideOneDrivePicker = (): void => {
    isMicrosoftOneDrivePickerVisible.value = false
  }
  const mountOneDrivePicker = (): void => {
    isMicrosoftOneDrivePickerOpen.value = true
    isMicrosoftOneDrivePickerVisible.value = true
  }
  const unmountOneDrivePicker = (): void => {
    isMicrosoftOneDrivePickerOpen.value = false
    isMicrosoftOneDrivePickerVisible.value = false
  }

  function promptConnectWithMicrosoftOneDriveModal(onSuccessCallback?: () => void): void {
    const payload: ConfirmationDialogPayload = {
      iconSrc: oneDriveColorSvg,
      title: __('Microsoft OneDrive is not connected yet'),
      body: __(
        'Connect your Microsoft account to import or link files from OneDrive. You will only have to do this once.',
      ),
      customBodyClasses: 'px-6',
      confirmButtonText: __('Connect'),
      cancelButtonText: __('Cancel'),
      afterConfirmActions: [
        async () => {
          const result = await useMicrosoftOneDrive().requestMicrosoftOneDriveAuthorization({})
          // Set OneDrive Email and token in frontend for file picker
          microsoftOneDriveEmail.value = result.email
          microsoftOneDriveAccessToken.value = result.token

          // Fetch additional data like base URL and account type
          await fetchMicrosoftOneDriveInfo()

          // Call success callback if provided
          if (onSuccessCallback) {
            onSuccessCallback()
          }
        },
      ],
      afterCancelActions: [],
      forceFullWidthButtons: true,
    }
    useGlobalConfirmationDialogStore().openConfirmationDialog(payload)
  }
  // #endregion

  return {
    // configuration
    microsoftOneDriveAccountType,
    acceptedFileTypes,
    fullPickerUrl,

    // authorization and fetching status
    isErroredFetchingMicrosoftOneDriveUserInformation,
    isFetchingMicrosoftOneDriveUserInformation,
    isUserLinkedWithMicrosoftOneDrive,
    microsoftOneDriveAccessToken,
    microsoftOneDriveEmail,
    fetchMicrosoftOneDriveInfo,
    clearMicrosoftOneDriveInfo,

    // message handling
    messagePort,
    setupPickerMessageListener,

    // picker visibility
    isMicrosoftOneDrivePickerOpen,
    isMicrosoftOneDrivePickerVisible,
    mountOneDrivePicker,
    promptConnectWithMicrosoftOneDriveModal,
    unmountOneDrivePicker,
  }
})
export {
    MicrosoftOneDriveAccountType,
    MicrosoftOneDriveUploadType,
    MicrosoftOneDriveAcceptedFileTypes,
    useMicrosoftOneDrivePickerStore,
}
